import React from 'react';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { ClientProductCalculation } from 'types/clientProductTypes';
import { useServices } from 'hooks/useServices';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { M8ProductType } from 'services/production/types';
import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { TextComponent } from 'design/text/textComponents';
import { FormControlComponent } from 'design/formControl';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { MarginComponent } from 'design/margin';
import { ClientProductFieldsetComponent } from 'containers/ClientProductFieldsetComponent';
import { useClientProductCalculation } from 'hooks/useClientProductCalculation';
import { useClientProductFormValues } from 'hooks/useClientProductFormValues';

type Props = {
  clientId: number;
  partnerId?: number;
  initialCalculation: ClientProductCalculation;
  m8ProductType: M8ProductType;
  onCancel(): void;
  onSubmitDidSucceed(): void;
};

export const ClientDetailsProductEditor = (props: Props) => {
  const {
    clientId,
    partnerId,
    initialCalculation,
    m8ProductType,
    onCancel,
    onSubmitDidSucceed,
  } = props;

  const { injectPromise } = useAsyncBoundary();

  const { submitClientProductForm } = useServices();

  const intl = useIntlDictionary();

  const {
    clientDetails: { productEditor: ownDictionary },
  } = intl;

  const {
    formValues: clientProductFormValues,
    setDiscountPercent,
    setPartnerPercent,
  } = useClientProductFormValues();

  const clientProductCalculation = useClientProductCalculation({
    clientId,
    m8ProductType: m8ProductType,
    initialCalculation: initialCalculation,
    formValues: clientProductFormValues,
  });

  React.useLayoutEffect(() => {
    setDiscountPercent(`${initialCalculation.discountPercent}`);

    if (partnerId) {
      setPartnerPercent(`${initialCalculation.partnerPercent}`);
    }
  }, [
    initialCalculation.discountPercent,
    initialCalculation.partnerPercent,
    partnerId,
    setDiscountPercent,
    setPartnerPercent,
  ]);

  const submit = React.useCallback(() => {
    const promise = submitClientProductForm({
      clientId,
      partnerId,
      formValues: clientProductFormValues,
      m8ProductType,
    }).then(onSubmitDidSucceed);

    injectPromise(promise, { shouldShowFallback: false });
  }, [
    clientId,
    clientProductFormValues,
    injectPromise,
    m8ProductType,
    onSubmitDidSucceed,
    partnerId,
    submitClientProductForm,
  ]);

  return (
    <>
      <SidebarModalSectionComponent
        theme="head"
        content={
          <TextComponent size="mpp" weight="medium">
            {ownDictionary.title}
          </TextComponent>
        }
        actions={null}
      />
      <FormControlComponent onSubmit={submit}>
        <SidebarModalSectionComponent
          content={
            <ClientProductFieldsetComponent
              values={clientProductFormValues}
              calculation={clientProductCalculation}
              m8ProductType={m8ProductType}
              withPartnerPercent={partnerId !== undefined}
              onDiscountPercentChange={setDiscountPercent}
              onPartnerPercentChange={setPartnerPercent}
            />
          }
          actions={
            <>
              <ModalPressControlComponent theme="primary" type="submit">
                {intl.saveChangesButton}
              </ModalPressControlComponent>
              <MarginComponent top={20} />
              <ModalPressControlComponent
                theme="secondary"
                type="button"
                onPress={onCancel}
              >
                {intl.cancelButton}
              </ModalPressControlComponent>
            </>
          }
        />
      </FormControlComponent>
    </>
  );
};
